import { useCallback, useEffect } from "react";
import { useClientId } from "./ClientConfig";
import { buildChannel, isRunningLocally, useDeepMemoValue } from "./util";
import amplitude from "amplitude-js";
import { useLocalUser } from "./pages/specialty-assistant/data";
import { useParams } from "react-router-dom";
import { User } from "./client";
import { AMPLITUDE_API_KEY } from "./config";

const instance = amplitude.getInstance();
instance.init(AMPLITUDE_API_KEY);

export const logEvent = (
  eventName: string,
  eventProperties?: { [field: string]: unknown }
) => {
  const allEventProperties = eventProperties ?? {};
  allEventProperties["build_channel"] = buildChannel();

  if (isRunningLocally()) {
    console.log(
      `Log ${eventName} to Amplitude with event properties`,
      eventProperties
    );
  }

  instance.logEvent(eventName, allEventProperties);
};

const createLogUIEventPayload = (
  clientId: string | undefined,
  localUser: User | undefined,
  reportId: string | undefined,
  eventName: string,
  eventProperties?: { [field: string]: unknown }
) => {
  const allEventProperties = { ...(eventProperties ?? {}) };
  if (clientId) {
    allEventProperties["pantheon_client_id"] = clientId;
  }
  if (localUser) {
    allEventProperties["pantheon_user_id"] = localUser.id;
    allEventProperties["pantheon_user_email"] = localUser.email;
    allEventProperties["user_id"] = localUser.id;
  }
  if (reportId) {
    allEventProperties["asgard_report_id"] = reportId;
  }

  return { eventName, allEventProperties };
};

// Hook that automatically logs on mount via useEffect
export const useLogUIEventOnMount = (
  eventName: string,
  eventProperties?: { [field: string]: unknown }
) => {
  const clientId = useClientId();
  const localUser = useLocalUser();
  const { reportId } = useParams<{ reportId: string }>();
  const properties = useDeepMemoValue(eventProperties);

  useEffect(() => {
    // Only log once we have these
    if (!clientId || !localUser) {
      return;
    }

    const { eventName: name, allEventProperties } = createLogUIEventPayload(
      clientId,
      localUser,
      reportId,
      eventName,
      properties
    );
    logEvent(name, allEventProperties);
  }, [clientId, localUser, reportId, eventName, properties]);
};

// Hook that returns a callable function
export const useLogUIEventCallback = () => {
  const clientId = useClientId();
  const localUser = useLocalUser();
  const { reportId } = useParams<{ reportId: string }>();

  return useCallback(
    (eventName: string, eventProperties?: { [field: string]: unknown }) => {
      const { eventName: name, allEventProperties } = createLogUIEventPayload(
        clientId,
        localUser,
        reportId,
        eventName,
        eventProperties
      );
      logEvent(name, allEventProperties);
    },
    [clientId, localUser, reportId]
  );
};
